// deps
import { useCallback, useRef } from "react";

// hooks
import { useMountedRef, useEffectOnce } from "@swiggy-private/core-ui";

// utils
import { poll } from "@payment-client/utils/Async";

export interface PollingFnData {
    pollingDurationInMs: number;
    pollingIntervalInMs: number;
    apiFn: () => Promise<void>;
}

type UsePollingReturnType = (pollingData: PollingFnData) => void;

const usePolling = (postPollingHandler?: VoidFunction): UsePollingReturnType => {
    const isMountedRef = useMountedRef();
    const stopPolling = useRef<VoidFunction>();

    const startPolling = useCallback(
        (pollingData: PollingFnData): void => {
            if (stopPolling.current) {
                stopPolling.current();
            }

            const { pollingDurationInMs, pollingIntervalInMs, apiFn } = pollingData;
            const [promise, cancel] = poll(pollingDurationInMs, pollingIntervalInMs, apiFn);

            promise
                .then(() => {
                    // Once the polling gets stopped (either polling API responded or timer timed out)
                    // callback function will be called if provided
                    if (isMountedRef.current && typeof postPollingHandler === "function") {
                        window.requestAnimationFrame(postPollingHandler);
                    }
                })
                .catch(() => void 0);

            stopPolling.current = cancel;
        },
        [stopPolling, postPollingHandler, isMountedRef],
    );

    useEffectOnce(() => {
        // is mounted check..
        if (!isMountedRef.current) {
            return;
        }

        // stops polling on unmount
        return (): void => {
            if (stopPolling.current != null) {
                stopPolling.current();
            }
        };
    });

    return startPolling;
};

export default usePolling;
